import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bVehicleReservation } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-reservations',
  templateUrl: './b2b-reservations.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-reservations.component.css']
})
export class B2bReservationsComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bVehicleReservation[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedVehicleId: number | null = null;
  form!: FormGroup;

  readonly statusOptions = [
    { value: 'Requested', label: 'Solicitada' },
    { value: 'Confirmed', label: 'Confirmada' },
    { value: 'InUse', label: 'Em uso' },
    { value: 'Completed', label: 'Concluída' },
    { value: 'Cancelled', label: 'Cancelada' }
  ];

  columns = [
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Reservado para', label: 'reservedFor' },
    { titulo: 'Solicitado em', label: 'requestedAt', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Início', label: 'startDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Fim', label: 'endDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Status', label: 'status', formatter: (v: any) => this.statusLabel(v) }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar reserva';
    return this.editingId ? 'Editar reserva' : 'Nova reserva';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      plate: [''],
      reservedFor: [''],
      requestedAt: [''],
      startDate: [''],
      endDate: [''],
      status: ['Requested', Validators.required],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bReservations(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
    this.form.reset({
      plate: '', reservedFor: '', requestedAt: '', startDate: '', endDate: '', status: 'Requested', notes: ''
    });
    this.showModal = true;
  }

  openView(item: B2bVehicleReservation): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bVehicleReservation): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bVehicleReservation): void {
    this.editingId = item.id ?? null;
    this.selectedVehicleId = item.vehicleId ?? null;
    this.form.patchValue({
      plate: item.plate || '',
      reservedFor: item.reservedFor || '',
      requestedAt: this.toDateInput(item.requestedAt),
      startDate: this.toDateInput(item.startDate),
      endDate: this.toDateInput(item.endDate),
      status: item.status || 'Requested',
      notes: item.notes || ''
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bVehicleReservation = {
      b2bContractId: this.contractId,
      vehicleId: this.selectedVehicleId,
      plate: raw.plate || null,
      reservedFor: raw.reservedFor || null,
      requestedAt: raw.requestedAt ? new Date(raw.requestedAt).toISOString() : null,
      startDate: raw.startDate ? new Date(raw.startDate).toISOString() : null,
      endDate: raw.endDate ? new Date(raw.endDate).toISOString() : null,
      status: raw.status || 'Requested',
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bReservation(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bReservation(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Reserva atualizada.' : 'Reserva criada.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bVehicleReservation }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bVehicleReservation): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.plate || item.reservedFor || 'esta reserva', 'a reserva').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bReservation(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Reserva excluída.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-reservas-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Reservas'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bVehicleReservation {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      plate: dto.plate ?? dto.Plate ?? null,
      reservedFor: dto.reservedFor ?? dto.ReservedFor ?? null,
      requestedAt: dto.requestedAt ?? dto.RequestedAt ?? null,
      startDate: dto.startDate ?? dto.StartDate ?? null,
      endDate: dto.endDate ?? dto.EndDate ?? null,
      status: dto.status ?? dto.Status ?? 'Requested',
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  statusLabel(value: string): string {
    return this.statusOptions.find(s => s.value === value)?.label || value || '—';
  }
}
